/**
 * 🔥 Прогрев клиентов — сценарии автосообщений бота.
 *
 * Список берётся с бэка (`application/warmup/registry.py`), здесь ничего не
 * захардкожено: новый сценарий появится сам. Партнёр правит текст и картинку и
 * может выключить сценарий; момент отправки — наш и не меняется.
 */
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type WarmupCatalog, type WarmupTrigger } from '@/lib/api';
import { Accordion } from '@/components/ui/accordion';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { TelegramEditor } from '@/components/text-editor/TelegramEditor';
import { getWebApp, haptic } from '@/lib/telegram';
import { toast } from 'sonner';
import {
  ArrowLeft, Check, Clock, Image as ImageIcon, Info, Loader2, RotateCcw,
} from 'lucide-react';
import { WarmupGroupIcon, WarmupIcon, groupTitle } from '@/lib/warmup-icons';

export const Route = createFileRoute('/bot-warmup/$botId')({ component: BotWarmup });

function BotWarmup() {
  const navigate = useNavigate();
  const { botId } = Route.useParams();

  const { data, isPending, isError } = useQuery({
    queryKey: ['bot-warmup', botId],
    queryFn: () => api.getBotWarmup(botId),
    retry: 1,
  });

  // Telegram BackButton → назад на карточку бота.
  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    let registered: (() => void) | null = null;
    const timer = window.setTimeout(() => {
      tg.BackButton.show();
      const onBack = () => navigate({ to: '/bot' });
      tg.BackButton.onClick(onBack);
      registered = onBack;
    }, 300);
    return () => {
      window.clearTimeout(timer);
      if (registered) tg.BackButton.offClick(registered);
      tg.BackButton.hide();
    };
  }, [navigate, botId]);

  const grouped = useMemo(() => {
    if (!data) return [];
    return data.groups
      .map((g) => ({
        ...g,
        triggers: data.triggers.filter((t) => t.group === g.key),
      }))
      .filter((g) => g.triggers.length > 0);
  }, [data]);

  const activeCount = data?.triggers.filter((t) => t.enabled).length ?? 0;
  const total = data?.triggers.length ?? 0;

  return (
    <div className="p-4 space-y-4 pb-32">
      <div className="px-1 pt-2 flex items-center gap-3">
        <Link
          to="/bot"
          className="w-9 h-9 rounded-[12px] surface-inset flex items-center justify-center shrink-0 active:scale-95 transition-transform"
          aria-label="Назад"
        >
          <ArrowLeft className="w-[18px] h-[18px]" />
        </Link>
        <h2 className="font-display text-lg font-semibold tracking-tight">Прогрев клиентов</h2>
      </div>

      <div className="flex gap-2.5 text-[12px] text-muted-foreground surface-inset rounded-[14px] p-3.5 leading-relaxed">
        <Info className="w-4 h-4 shrink-0 mt-px text-primary-light" />
        <div>
          Бот сам пишет клиенту в нужный момент: не активировал пробник, не подключился,
          заканчивается подписка, давно не возвращался.
          {total > 0 && (
            <>
              {' '}
              Сейчас работает <b className="text-foreground">{activeCount}</b> из {total}.
            </>
          )}
          <div className="mt-1.5 text-dim">
            Тексты и картинки — ваши. Момент отправки менять нельзя: сдвинутое напоминание
            об окончании подписки бесполезно.
          </div>
        </div>
      </div>

      {isPending && (
        <div className="space-y-3">
          <Skeleton className="h-14 rounded-[14px]" />
          <Skeleton className="h-14 rounded-[14px]" />
          <Skeleton className="h-14 rounded-[14px]" />
        </div>
      )}

      {isError && (
        <p className="text-[13px] text-danger px-1">
          Не удалось загрузить сценарии. Обновите страницу.
        </p>
      )}

      {grouped.map((g) => (
        <Accordion
          key={g.key}
          title={groupTitle(g.label)}
          icon={<WarmupGroupIcon groupKey={g.key} className="w-4 h-4" />}
        >
          <div className="space-y-3">
            {g.triggers.map((t) => (
              <TriggerCard key={t.key} botId={botId} trigger={t} />
            ))}
          </div>
        </Accordion>
      ))}
    </div>
  );
}

/** Одна карточка сценария: тумблер, текст, картинка. */
function TriggerCard({ botId, trigger }: { botId: string; trigger: WarmupTrigger }) {
  const qc = useQueryClient();
  // Переменные имени приходят в том же каталоге, что и сценарии (#206).
  const userVars = (qc.getQueryData(['bot-warmup', botId]) as WarmupCatalog | undefined)?.user_vars;
  const fileRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(trigger.text);
  const [editorKey, setEditorKey] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [offerInput, setOfferInput] = useState(String(trigger.offer_percent ?? ''));
  useEffect(() => {
    setOfferInput(String(trigger.offer_percent ?? ''));
  }, [trigger.offer_percent]);

  const onCatalog = (cat: WarmupCatalog) => qc.setQueryData(['bot-warmup', botId], cat);

  const extractMsg = (e: unknown): string => {
    if (e instanceof ApiError && e.status === 422) {
      const body = e.body as
        | { message?: string; detail?: string; error?: { message?: string } }
        | null;
      const msg = body?.error?.message ?? body?.message ?? body?.detail ?? e.message;
      return typeof msg === 'string' ? msg : 'Не прошло проверку';
    }
    return e instanceof Error ? e.message : 'Не сохранилось';
  };

  const patch = useMutation({
    mutationFn: (body: {
      text?: string | null;
      enabled?: boolean;
      media_url?: string | null;
      offer_percent?: number | null;
    }) =>
      api.updateBotWarmup(botId, { key: trigger.key, ...body }),
    onSuccess: (cat) => {
      onCatalog(cat);
      setErr(null);
      haptic('success');
    },
    onError: (e) => {
      setErr(extractMsg(e));
      haptic('error');
    },
  });

  const upload = useMutation({
    mutationFn: (file: File) => api.uploadWarmupImage(botId, trigger.key, file),
    onSuccess: (cat) => {
      onCatalog(cat);
      haptic('success');
      toast.success('Картинка сохранена');
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(
        e.message.includes('disabled') ? 'Загрузка фото не настроена на сервере' : 'Не загрузилось',
      );
    },
  });

  const busy = patch.isPending || upload.isPending;
  const dirty = draft !== trigger.text;

  return (
    <div className="surface-inset rounded-[14px] p-3.5 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-bold text-[13.5px] flex items-center gap-1.5">
            <WarmupIcon triggerKey={trigger.key} className="w-4 h-4 shrink-0 text-primary-light" />
            {trigger.label}
          </div>
          <div className="text-[11px] text-dim mt-1 flex items-start gap-1.5">
            <Clock className="w-3 h-3 shrink-0 mt-px" />
            <span>{trigger.schedule_hint}</span>
          </div>
        </div>
        <Switch
          checked={trigger.enabled}
          disabled={busy}
          aria-label={trigger.label}
          onCheckedChange={(v) => {
            patch.mutate({ enabled: v });
            toast.success(v ? 'Сценарий включён' : 'Сценарий выключен');
          }}
        />
      </div>

      {!trigger.enabled && (
        <p className="text-[11.5px] text-muted-foreground leading-snug">
          Выключен — клиентам не уходит. Текст ниже сохранится и включится вместе со сценарием.
        </p>
      )}

      {/* Персональная скидка к сообщению: код выдаётся при отправке и применяется сам
          на оплате. Платит партнёр из своей наценки по той покупке. */}
      <div className="surface-inset rounded-[14px] p-3 space-y-2">
        <div className="flex items-center gap-2">
          <div className="flex-1 text-[12.5px] font-semibold">Скидка к сообщению</div>
          <input
            type="number"
            min={0}
            max={50}
            value={offerInput}
            placeholder="нет"
            onChange={(e) => setOfferInput(e.target.value)}
            className="w-20 surface-card rounded-[10px] px-2.5 py-2 text-sm text-right focus:outline-none"
          />
          <span className="text-[12.5px] text-dim">%</span>
        </div>
        <button
          type="button"
          disabled={busy || String(trigger.offer_percent ?? '') === offerInput.trim()}
          onClick={() => {
            const raw = offerInput.trim();
            const parsed = raw === '' ? null : Math.max(0, Math.min(Number(raw) || 0, 50));
            patch.mutate({ offer_percent: parsed === 0 ? null : parsed });
          }}
          className="w-full bg-white/[0.07] py-2.5 rounded-[11px] text-[13px] font-bold disabled:opacity-40 active:scale-[0.99] transition-transform"
        >
          Сохранить скидку
        </button>
        <p className="text-[11px] text-dim leading-relaxed">
          Клиент получит персональный код на 48 часов, он применится сам при оплате.
          Скидку оплачиваете вы из своей наценки по той покупке, а глубже наценки разницу
          спишем с баланса. Денег не хватит, и клиент увидит полную цену.
        </p>
      </div>

      <TelegramEditor
        key={editorKey}
        value={trigger.text}
        placeholders={trigger.placeholders}
        userVars={userVars}
        onChange={(html) => {
          setDraft(html);
          if (err) setErr(null);
        }}
        maxLen={trigger.max_len}
      />

      {err && <p className="text-[12px] text-danger leading-snug">{err}</p>}

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={busy || !dirty}
          onClick={() =>
            patch.mutate(
              { text: draft },
              { onSuccess: () => toast.success('Сохранено') },
            )
          }
          className="flex-1 bg-primary text-primary-foreground py-2.5 rounded-[12px] text-[13.5px] font-bold flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.98] transition-transform"
        >
          {patch.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Check className="w-4 h-4" />
          )}
          Сохранить
        </button>
        <button
          type="button"
          disabled={busy || !trigger.is_custom}
          onClick={() =>
            patch.mutate(
              { text: null },
              {
                onSuccess: (cat) => {
                  const fresh = cat.triggers.find((t) => t.key === trigger.key);
                  setDraft(fresh?.text ?? trigger.default_text);
                  setEditorKey((k) => k + 1);
                  toast.success('Вернули наш текст');
                },
              },
            )
          }
          title="Вернуть наш текст"
          aria-label="Вернуть наш текст"
          className="w-11 h-11 rounded-[12px] surface-inset flex items-center justify-center disabled:opacity-40 active:scale-95 transition-transform"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>

      {/* Картинка. Уходит подписью под фото; если текст длиннее лимита подписи
          Telegram — сообщение уйдёт без картинки, но целиком. */}
      <div className="space-y-2">
        {trigger.media_url ? (
          <div className="relative aspect-video rounded-lg overflow-hidden bg-black/30 border border-white/5">
            <img src={trigger.media_url} alt="" className="w-full h-full object-cover" />
          </div>
        ) : null}
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) upload.mutate(f);
            e.target.value = '';
          }}
          className="hidden"
        />
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="flex-1 bg-white/[0.04] hover:bg-white/[0.06] py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {upload.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <ImageIcon className="w-3.5 h-3.5" />
            )}
            {trigger.media_url ? 'Заменить картинку' : 'Добавить картинку'}
          </button>
          {trigger.media_url && (
            <button
              type="button"
              onClick={() =>
                patch.mutate(
                  { media_url: null },
                  { onSuccess: () => toast.success('Картинку убрали') },
                )
              }
              disabled={busy}
              className="px-3 py-2 rounded-lg text-[11px] font-bold text-danger disabled:opacity-50"
            >
              Убрать
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
